import type React from 'react';

/**
 * Whether a row click belongs to something other than the row: a link or
 * control inside a cell, or the end of a text selection. The table's own row
 * links sit directly in their cell and don't count. The asset name opens the
 * asset from a plain span, so it is matched by its test id.
 */
export const isCellControlClick = (event: React.MouseEvent<HTMLElement>) => {
  const selection = window.getSelection();
  if (
    selection &&
    !selection.isCollapsed &&
    selection.anchorNode &&
    event.currentTarget.contains(selection.anchorNode)
  ) {
    return true;
  }
  const control = (event.target as Element).closest(
    'a, button, input, select, textarea, [role="button"], [role="link"], [data-testid="asset-name"]',
  );
  return (
    !!control &&
    event.currentTarget.contains(control) &&
    control.parentElement?.tagName !== 'TD'
  );
};

/**
 * A row drawn as a card under the 'rounded' variant that opens something on
 * click. It glows like the home page's Focus here cards on hover and when its
 * link has keyboard focus, and its cells stay white rather than turning the
 * table's grey. A drop shadow, since a row's box shadow ignores the card's
 * rounded corners.
 *
 * The first cell's content fills the row's height, so it can keep its last
 * line at the bottom when another column is taller. That needs the table to
 * carry CARD_TABLE_CLASSNAME: with a 1px table and a 100% cell, Chrome and
 * Firefox both resolve the cell's content against the row's real height. A
 * 1px cell instead works only in Chrome, and collapses the row in Firefox.
 */
export const CARD_ROW_CLASSNAME =
  'hover:drop-shadow-action-hover hover:[&>td]:bg-white dark:hover:[&>td]:bg-gray-900 has-[a:focus-visible]:drop-shadow-action-hover [&>td:first-child]:h-full [&>td:first-child>div]:h-full';

/** The table of card rows; see CARD_ROW_CLASSNAME. It still grows to fit. */
export const CARD_TABLE_CLASSNAME = 'h-px';

/**
 * A sticky header's offset: under whatever its scroll box keeps pinned above
 * it, such as the app's banners or a panel's header, which publish their
 * height as --app-sticky-top.
 */
export const STICKY_HEADER_CLASSNAME = 'top-[var(--app-sticky-top,0px)]';
